import { axiosClient } from "../api/axiosClient";

// Types matching the API response
export interface MediumPoints {
  id: string;
  mediumId: string;
  terreiroId: string;
  totalPoints: number;
  weeklyPoints: number;
  monthlyPoints: number;
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  monthlyProgress?: {
    timezone: string;
    monthStart: string;
    monthEnd: string;
    today: string;
    daysAccessed: number;
    totalDays: number;
    bonusUnlocked: boolean;
    bonusAwarded: boolean;
    bonusPoints: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PointTransaction {
  id: string;
  mediumId: string;
  points: number;
  type: string;
  description: string;
  referenceId: string | null;
  referenceType: string | null;
  createdAt: string;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  type: string;
  tier: string;
  requirement: string;
  pointsRequired: number | null;
  isActive: boolean;
}

export interface MediumBadge extends Badge {
  earnedAt: string;
}

export interface BadgeProgress {
  badge: Badge;
  earned: boolean;
  progress: number;
  earnedAt?: string;
}

export interface RankingEntry {
  // Legacy app fields
  rank: number;
  previousRank: number | null;
  position: number;
  previousPosition: number | null;
  mediumId: string;
  mediumName: string;
  mediumAvatar: string | null;
  avatarUrl: string | null;
  terreiroName?: string;
  points: number;
  streak: number;
  badges: number;
  isCurrentUser: boolean;
  participationCount: number;
  positionChange: number;
}

export interface RankingResult {
  scope: string;
  scopeId: string | null;
  period: string;
  entries: RankingEntry[];
  // Legacy app fields
  totalParticipants: number;
  currentUserPosition: number | null;
  lastUpdated: string;
  totalEntries: number;
  calculatedAt: string;
}

export interface GamificationSummary {
  points: MediumPoints;
  position: {
    rank: number;
    totalParticipants: number;
    change: number | null;
  } | null;
  recentBadges: MediumBadge[];
  totalBadges: number;
}

// Ranking scopes and periods
export type RankingScope = "TERREIRO" | "CITY" | "STATE" | "COUNTRY";
export type RankingPeriod = "WEEKLY" | "MONTHLY" | "YEARLY" | "ALL_TIME";

export const gamificationRepository = {
  // Get medium's points summary
  getPoints: async (
    terreiroId: string,
    mediumId: string,
  ): Promise<MediumPoints> => {
    const response = await axiosClient.get<MediumPoints>(
      `/gamification/terreiros/${terreiroId}/mediums/${mediumId}/points`,
    );
    return response.data;
  },

  // Get point transaction history
  getPointHistory: async (
    terreiroId: string,
    mediumId: string,
    limit?: number,
  ): Promise<PointTransaction[]> => {
    const params = limit ? { limit } : {};
    const response = await axiosClient.get<PointTransaction[]>(
      `/gamification/terreiros/${terreiroId}/mediums/${mediumId}/points/history`,
      { params },
    );
    return response.data;
  },

  // Get rankings by scope
  getRankings: async (
    scope: RankingScope,
    scopeId: string | null,
    period: RankingPeriod,
    mediumId?: string,
  ): Promise<RankingResult> => {
    const params: Record<string, string | undefined> = {
      scope: scope,
      period,
      mediumId,
    };

    if (scopeId) {
      params.scopeId = scopeId;
    }

    const response = await axiosClient.get<{
      scope: string;
      scopeId: string | null;
      period: string;
      entries: Array<{
        position: number;
        previousPosition: number | null;
        mediumId: string;
        mediumName: string;
        avatarUrl: string | null;
        terreiroName?: string;
        points: number;
        participationCount: number;
        positionChange: number;
      }>;
      totalEntries: number;
      calculatedAt: string;
    }>(
      `/gamification/rankings`,
      { params },
    );

    const normalizedEntries: RankingEntry[] = response.data.entries.map((entry) => ({
      rank: entry.position,
      previousRank: entry.previousPosition,
      position: entry.position,
      previousPosition: entry.previousPosition,
      mediumId: entry.mediumId,
      mediumName: entry.mediumName,
      mediumAvatar: entry.avatarUrl,
      avatarUrl: entry.avatarUrl,
      terreiroName: entry.terreiroName,
      points: entry.points,
      streak: 0,
      badges: 0,
      isCurrentUser: mediumId ? entry.mediumId === mediumId : false,
      participationCount: entry.participationCount,
      positionChange: entry.positionChange,
    }));

    const currentUserPosition =
      mediumId
        ? normalizedEntries.find((entry) => entry.mediumId === mediumId)?.position ?? null
        : null;

    return {
      scope: response.data.scope,
      scopeId: response.data.scopeId,
      period: response.data.period,
      entries: normalizedEntries,
      totalParticipants: response.data.totalEntries,
      currentUserPosition,
      lastUpdated: response.data.calculatedAt,
      totalEntries: response.data.totalEntries,
      calculatedAt: response.data.calculatedAt,
    };
  },

  // Get terreiro rankings (convenience method)
  getTerreiroRankings: async (
    terreiroId: string,
    period: RankingPeriod = "MONTHLY",
    mediumId?: string,
  ): Promise<RankingResult> => {
    return gamificationRepository.getRankings(
      "TERREIRO",
      terreiroId,
      period,
      mediumId,
    );
  },

  // Get city rankings
  getCityRankings: async (
    city: string,
    period: RankingPeriod = "MONTHLY",
    mediumId?: string,
  ): Promise<RankingResult> => {
    return gamificationRepository.getRankings("CITY", city, period, mediumId);
  },

  // Get state rankings
  getStateRankings: async (
    state: string,
    period: RankingPeriod = "MONTHLY",
    mediumId?: string,
  ): Promise<RankingResult> => {
    return gamificationRepository.getRankings("STATE", state, period, mediumId);
  },

  // Get country rankings
  getCountryRankings: async (
    country: string,
    period: RankingPeriod = "MONTHLY",
    mediumId?: string,
  ): Promise<RankingResult> => {
    return gamificationRepository.getRankings(
      "COUNTRY",
      country,
      period,
      mediumId,
    );
  },

  // Get all badges
  getAllBadges: async (): Promise<Badge[]> => {
    const response = await axiosClient.get<Badge[]>(`/v1/gamification/badges`);
    return response.data;
  },

  // Get badges for a medium
  getMediumBadges: async (mediumId: string): Promise<MediumBadge[]> => {
    const response = await axiosClient.get<MediumBadge[]>(
      `/v1/gamification/badges/${mediumId}`,
    );
    return response.data;
  },

  // Get badge progress for a medium
  getBadgeProgress: async (
    terreiroId: string,
    mediumId: string,
  ): Promise<BadgeProgress[]> => {
    const response = await axiosClient.get<BadgeProgress[]>(
      `/gamification/terreiros/${terreiroId}/mediums/${mediumId}/badges/progress`,
    );
    return response.data;
  },

  // Get gamification summary for a medium
  getSummary: async (
    terreiroId: string,
    mediumId: string,
  ): Promise<GamificationSummary> => {
    const response = await axiosClient.get<GamificationSummary>(
      `/gamification/terreiros/${terreiroId}/mediums/${mediumId}/gamification`,
    );
    return response.data;
  },
};
